import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { createLogger } from '../../utils/logger';
import { Stepper } from '../components/Stepper';
import { createAdsJob, fetchAdsContext, uploadLogo } from '../api';
import type {
  AdDesign,
  AdInfo,
  AdsContext,
  LogoCorner,
  Mood,
  Orientation,
  PalettePreset,
  Style,
  VehicleFieldSource,
  VehicleMetadata,
} from '../types';

const log = createLogger('AdsGenerate');

type Step = 1 | 2 | 3 | 4;

// LocalStorage key for persisting in-flight wizard state across reloads.
// We deliberately omit the submitted ``job_id`` from this payload — once
// a job has been queued the operator should track it on the History /
// Live Scans pages, not by re-landing on a stale Step-5 review here.
//
// Bump the version suffix whenever the default state shape changes in a
// way that older persisted blobs would mask (e.g. when introducing new
// dev-default placeholder values for the Step-2 listing form). Any
// pre-existing state under the old key is silently dropped on next
// load, which is fine — the wizard is short-lived and easy to refill.
const STORAGE_KEY = 'ads.wizard.state.v2';

// How long the post-submit success banner sticks before auto-hiding.
// 8s is long enough to read and click through to /ads/history but
// short enough that it never overlaps a *next* submission attempt.
const SUCCESS_BANNER_MS = 8000;

const ORIENTATIONS: { id: Orientation; label: string }[] = [
  { id: 'front', label: 'Front' },
  { id: 'left_front', label: 'Left front' },
  { id: 'right_front', label: 'Right front' },
  { id: 'side_left', label: 'Side left' },
  { id: 'side_right', label: 'Side right' },
];
const MOODS: Mood[] = ['sport', 'luxury', 'family', 'work', 'offroad', 'casual'];
const STYLES: Style[] = ['classic', 'modern', 'minimal', 'bold'];
const LOGO_CORNERS: { id: LogoCorner; label: string }[] = [
  { id: 'tl', label: 'Top left' },
  { id: 'tr', label: 'Top right' },
  { id: 'bl', label: 'Bottom left' },
  { id: 'br', label: 'Bottom right' },
  { id: 'none', label: 'No overlay' },
];

// Short blurbs surfaced under the selected mood / style chip so the
// operator gets a hint of what the engine will actually render before
// firing a generate. Kept intentionally brief — full prompt text lives
// server-side in ``config_ads.yaml``.
const MOOD_DESCRIPTIONS: Record<Mood, string> = {
  sport: 'Dynamic and energetic — bold lines, dramatic lighting, motion-blur hints.',
  luxury: 'Premium and refined — glossy surfaces, soft highlights, upscale setting.',
  family: 'Warm and approachable — friendly tones, safe daylight, welcoming vibe.',
  work: 'Rugged and capable — utilitarian backdrop, hard-working tool-of-the-trade feel.',
  offroad: 'Adventurous and rugged — outdoor terrain, dramatic landscape, off-road grit.',
  casual: 'Relaxed and everyday — neutral background, soft natural light, easygoing tone.',
};

const STYLE_DESCRIPTIONS: Record<Style, string> = {
  classic: 'Timeless composition — traditional layout with elegant, restrained typography.',
  modern: 'Clean and contemporary — sharp lines, balanced negative space, crisp graphics.',
  minimal: 'Stripped down — generous whitespace, subtle accents, lots of breathing room.',
  bold: 'High-impact — strong contrast, large typography, attention-grabbing graphics.',
};

// Per-orientation preference list driving the Step-3 thumbnail picker.
//
// UV-Paint merch only publishes four flat views — ``center_front``,
// ``center_rear``, ``center_left``, ``center_right``. For the
// front-quarter orientations (``left_front`` / ``right_front``) there
// is no exact merch shot, so we surface ``center_front`` (the closest
// merchandising view: clean white background, vehicle facing camera)
// rather than substituting the side-view shots. Atlas cover crops act
// as a structured fallback when merch is unavailable, and the wide
// ``overview`` image is the last resort.
const MERCH_ORIENTATION_KEYS: Record<Orientation, string[]> = {
  front: ['center_front'],
  left_front: ['front_left'],
  right_front: ['front_right'],
  side_left: ['center_left'],
  side_right: ['center_right'],
};

const COVER_ORIENTATION_KEYS: Record<Orientation, string[]> = {
  front: ['front', 'overview'],
  left_front: ['left_front', 'front', 'overview'],
  right_front: ['right_front', 'front', 'overview'],
  side_left: ['left_front', 'left_rear', 'overview'],
  side_right: ['right_front', 'right_rear', 'overview'],
};

function pickOrientationThumb(
  orientation: Orientation,
  merchImages: Record<string, string>,
  coverImages: Record<string, string>,
): string | null {
  for (const key of MERCH_ORIENTATION_KEYS[orientation]) {
    const url = merchImages[key];
    if (url) return url;
  }
  for (const key of COVER_ORIENTATION_KEYS[orientation]) {
    const url = coverImages[key];
    if (url) return url;
  }
  return null;
}

interface WizardState {
  scanId: string;
  metadata: VehicleMetadata;
  info: AdInfo;
  design: Partial<AdDesign>;
}

const EMPTY_METADATA: VehicleMetadata = {
  make: null,
  model: null,
  year: null,
  color: null,
  body_type: null,
  fuel_type: null,
  vin: null,
  trim: null,
};

const EMPTY_INFO: AdInfo = {
  mileage: '',
  price: '',
  location: '',
  phone: '',
  email: '',
  free_text: '',
};

// Dummy listing details surfaced as the Step-2 defaults when the build
// flag ``VITE_ADS_DEV_DEFAULTS`` is "1" (set by ``make deploy`` /
// ``make deploy-dev``, never by the production GitHub-Actions
// pipeline). They speed up local QA so testers don't retype the same
// throwaway values for every scan.
const DEV_DEFAULT_INFO: AdInfo = {
  mileage: '23,616 mi',
  price: '$28,995',
  location: 'Detroit, MI',
  phone: '(555) 123-4567',
  email: 'sales@example.com',
  free_text:
    'Low Monthly Payments & Flexible Financing. We Work With All Budgets & Credit Scores. Call to Action: Ask About Our Seasonal Specials.',
};

// Throwaway listing bundles behind the Step-2 "Randomize" button. Each
// entry is a self-consistent fake listing so QA / demo runs can fill the
// form with one click instead of retyping plausible values.
const RANDOM_INFO_OPTIONS: AdInfo[] = [
  {
    price: '$28,995',
    mileage: '23,616 mi',
    location: 'Detroit, MI',
    phone: '(313) 555-0142',
    email: 'sales@motorcitymotors.com',
    free_text:
      'Low monthly payments & flexible financing. We work with all budgets & credit scores. Ask about our seasonal specials!',
  },
  {
    price: '$41,250',
    mileage: '12,480 mi',
    location: 'Austin, TX',
    phone: '(512) 555-0188',
    email: 'hello@lonestarautos.com',
    free_text:
      'One owner, clean CARFAX, and still under factory warranty. Schedule your test drive today.',
  },
  {
    price: '$17,499',
    mileage: '68,902 mi',
    location: 'Phoenix, AZ',
    phone: '(602) 555-0119',
    email: 'desk@desertdriveauto.com',
    free_text:
      'Budget-friendly and fully inspected. Trade-ins welcome — get top dollar for your current ride.',
  },
  {
    price: '$54,800',
    mileage: '8,210 mi',
    location: 'Seattle, WA',
    phone: '(206) 555-0177',
    email: 'team@emeraldcitymotors.com',
    free_text:
      'Loaded with premium options and like-new condition. Nationwide shipping available.',
  },
  {
    price: '$22,340',
    mileage: '45,037 mi',
    location: 'Miami, FL',
    phone: '(305) 555-0163',
    email: 'sales@sunshinestateauto.com',
    free_text:
      'Certified pre-owned with a 7-day return guarantee. Reserve online with no obligation.',
  },
  {
    price: '$36,975',
    mileage: '31,290 mi',
    location: 'Denver, CO',
    phone: '(720) 555-0150',
    email: 'info@milehighmotors.com',
    free_text:
      'Adventure-ready with all-wheel drive. 0% APR financing available for qualified buyers.',
  },
  {
    price: '$13,995',
    mileage: '82,615 mi',
    location: 'Columbus, OH',
    phone: '(614) 555-0134',
    email: 'sales@buckeyeautogroup.com',
    free_text:
      'Great commuter car with excellent fuel economy. Easy approvals — apply in minutes.',
  },
];

// Read at module load — Vite inlines `import.meta.env.*` at build time
// so this is effectively a constant.
const USE_DEV_DEFAULTS = import.meta.env.VITE_ADS_DEV_DEFAULTS === '1';

const DEFAULT_INFO: AdInfo = USE_DEV_DEFAULTS
  ? { ...DEV_DEFAULT_INFO }
  : { ...EMPTY_INFO };

const EMPTY_STATE: WizardState = {
  scanId: '',
  metadata: { ...EMPTY_METADATA },
  info: { ...DEFAULT_INFO },
  design: {
    orientation: 'front',
    mood: 'sport',
    style: 'modern',
    palette: '',
    formats: [],
    logo_corner: 'none',
  },
};

function loadPersistedState(): WizardState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...EMPTY_STATE };
    const parsed = JSON.parse(raw) as Partial<WizardState>;
    return { ...EMPTY_STATE, ...parsed } as WizardState;
  } catch {
    return { ...EMPTY_STATE };
  }
}

function persistState(state: WizardState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota / serialization errors
  }
}

function clearPersistedState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// =============================================================================
// Step 1 — Context
// =============================================================================

function FieldSourceBadge({ source }: { source: VehicleFieldSource }) {
  if (source === 'uvcamp') {
    return (
      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-700">
        UVCamp
      </span>
    );
  }
  if (source === 'gemini') {
    return (
      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700">
        AI
      </span>
    );
  }
  return (
    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500">
      Missing
    </span>
  );
}

function ContextStep({
  scanId,
  setScanId,
  context,
  setContext,
  metadata,
  setMetadata,
  onNext,
}: {
  scanId: string;
  setScanId: (s: string) => void;
  context: AdsContext | null;
  setContext: (c: AdsContext | null) => void;
  metadata: VehicleMetadata;
  setMetadata: (m: VehicleMetadata) => void;
  onNext: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showValidate, setShowValidate] = useState(false);

  const handleFetch = useCallback(async () => {
    if (!scanId.trim()) {
      setError('Enter a scan ID');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const ctx = await fetchAdsContext(scanId.trim());
      setContext(ctx);
      // Seed editable metadata with whatever the backend resolved.
      setMetadata({ ...EMPTY_METADATA, ...ctx.vehicle_info });
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : 'Failed to fetch context');
      setContext(null);
    } finally {
      setLoading(false);
    }
  }, [scanId, setContext, setMetadata]);

  const handleMetaChange = (key: keyof VehicleMetadata, value: string) => {
    setMetadata({ ...metadata, [key]: value || null });
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5">
        <h2 className="text-base font-semibold text-slate-900 mb-3">Scan</h2>
        <div className="flex items-center gap-2">
          <input
            value={scanId}
            onChange={(e) => setScanId(e.target.value)}
            placeholder="Enter scan ID…"
            className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 font-mono"
          />
          <button
            onClick={handleFetch}
            disabled={loading || !scanId.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Fetch'}
          </button>
        </div>
        {error && (
          <div className="mt-3 px-3 py-2 rounded bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>

      {context && (
        <>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5">
            <h2 className="text-base font-semibold text-slate-900 mb-3">
              Vehicle metadata
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {(['make', 'model', 'year', 'color', 'body_type', 'fuel_type', 'trim', 'vin'] as (keyof VehicleMetadata)[]).map(
                (field) => (
                  <div key={field}>
                    <div className="flex items-center gap-2 mb-1">
                      <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                        {field.replace('_', ' ')}
                      </label>
                      <FieldSourceBadge
                        source={
                          (context.vehicle_info_source[
                            field as keyof typeof context.vehicle_info_source
                          ] as VehicleFieldSource) ?? 'missing'
                        }
                      />
                    </div>
                    <input
                      value={metadata[field] ?? ''}
                      onChange={(e) => handleMetaChange(field, e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      placeholder={field}
                    />
                  </div>
                ),
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => setShowValidate(true)}
              className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
            >
              Next
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5">
            <h2 className="text-base font-semibold text-slate-900 mb-3">Images</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {Object.entries({ ...context.merch_images, ...context.cover_images })
                .filter(([key, url]) => !!url && key !== 'overview' && key !== 'front')
                .map(([key, url]) => (
                  <div
                    key={`${key}-${url}`}
                    className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50"
                  >
                    <div className="aspect-video bg-slate-100">
                      <img
                        src={url}
                        alt={key}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="px-2 py-1 text-xs text-slate-600 font-medium">
                      {key}
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => setShowValidate(true)}
              className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
            >
              Next
            </button>
          </div>
        </>
      )}

      {showValidate && context && (
        <MetadataValidationModal
          metadata={metadata}
          onCancel={() => setShowValidate(false)}
          onConfirm={() => {
            setShowValidate(false);
            onNext();
          }}
        />
      )}
    </div>
  );
}

function MetadataValidationModal({
  metadata,
  onCancel,
  onConfirm,
}: {
  metadata: VehicleMetadata;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Confirm vehicle metadata
        </h3>
        <p className="text-sm text-slate-600 mb-4">
          The values below will be used in the prompt. Please double-check before
          continuing.
        </p>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mb-6">
          {(Object.keys(metadata) as (keyof VehicleMetadata)[]).map((field) => (
            <div key={field} className="contents">
              <dt className="text-slate-500 capitalize">{field.replace('_', ' ')}</dt>
              <dd className="text-slate-900 font-medium">
                {metadata[field] || '—'}
              </dd>
            </div>
          ))}
        </dl>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg"
          >
            Back to edit
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg"
          >
            Looks good — next
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Step 2 — Information
// =============================================================================

function InfoStep({
  info,
  setInfo,
  onBack,
  onNext,
}: {
  info: AdInfo;
  setInfo: (i: AdInfo) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const handleRandomize = () => {
    const chosen =
      RANDOM_INFO_OPTIONS[Math.floor(Math.random() * RANDOM_INFO_OPTIONS.length)];
    setInfo({ ...chosen });
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-slate-900">Listing details</h2>
          <button
            onClick={handleRandomize}
            className="px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md"
          >
            Randomize
          </button>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          These values are interpolated into the engine prompt. Skip any you don't
          want to advertise.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(
            [
              ['price', 'Price'],
              ['mileage', 'Mileage'],
              ['location', 'Location'],
              ['phone', 'Phone'],
              ['email', 'Email'],
            ] as [keyof AdInfo, string][]
          ).map(([key, label]) => (
            <div key={key}>
              <label className="text-xs font-medium text-slate-600 uppercase tracking-wide block mb-1">
                {label}
              </label>
              <input
                value={(info[key] as string | null) ?? ''}
                onChange={(e) => setInfo({ ...info, [key]: e.target.value || null })}
                className="w-full px-2 py-1 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                placeholder={label}
              />
            </div>
          ))}
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wide block mb-1">
              Free text
            </label>
            <textarea
              value={info.free_text ?? ''}
              onChange={(e) =>
                setInfo({ ...info, free_text: e.target.value || null })
              }
              rows={3}
              className="w-full px-2 py-1 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
              placeholder="Anything the AI should emphasise…"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md"
        >
          Back
        </button>
        <button
          onClick={onNext}
          className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
        >
          Next
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Step 3 — Design
// =============================================================================

function chipClass(isActive: boolean): string {
  return `px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
    isActive
      ? 'bg-indigo-600 text-white border-indigo-600'
      : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-300'
  }`;
}

function PaletteSwatch({
  palette,
  isActive,
  isRecommended,
  onClick,
}: {
  palette: PalettePreset;
  isActive: boolean;
  isRecommended: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`group relative flex flex-col items-stretch text-left transition-transform ${
        isActive ? 'scale-[1.01]' : 'hover:scale-[1.01]'
      }`}
    >
      <div
        className={`flex h-20 rounded-lg overflow-hidden border transition-shadow ${
          isActive
            ? 'border-indigo-500 ring-2 ring-indigo-200 shadow-md'
            : 'border-slate-200 group-hover:shadow-md'
        }`}
      >
        {palette.swatches.map((c, i) => (
          <div key={`${c}-${i}`} className="flex-1" style={{ background: c }} />
        ))}
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        <span className="text-sm text-slate-800 truncate">{palette.label}</span>
        {isRecommended && (
          <span
            className="text-amber-500 text-xs"
            title="Recommended for this vehicle"
          >
            ★
          </span>
        )}
      </div>
    </button>
  );
}

function DesignStep({
  context,
  design,
  setDesign,
  logoFile,
  setLogoFile,
  onBack,
  onNext,
}: {
  context: AdsContext;
  design: Partial<AdDesign>;
  setDesign: (d: Partial<AdDesign>) => void;
  logoFile: File | null;
  setLogoFile: (f: File | null) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  // Materialise the uploaded logo as a blob URL so we can render an
  // immediate preview. ``URL.createObjectURL`` allocates memory that
  // only the GC will reclaim — explicitly revoke on file change /
  // unmount to keep things tidy across repeated uploads.
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!logoFile) {
      setLogoPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(logoFile);
    setLogoPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  const recommended = useMemo(
    () => new Set(context.recommended_palettes),
    [context.recommended_palettes],
  );
  const recommendedPalettes = useMemo(
    () => context.palettes.filter((p) => recommended.has(p.id)),
    [context.palettes, recommended],
  );
  const otherPalettes = useMemo(
    () => context.palettes.filter((p) => !recommended.has(p.id)),
    [context.palettes, recommended],
  );

  // Default to recommendations-only when we actually have some; if the
  // backend returned no recommendations at all, fall back to showing
  // every palette right away so the operator isn't stuck on an empty
  // grid. Persisted state from a prior session never overrides this --
  // even if the previously-selected palette is not in the recommended
  // subset, we keep the grid collapsed and surface the selection via
  // the "Show all palettes" button (which always remains one click
  // away).
  const [showAllPalettes, setShowAllPalettes] = useState(
    recommendedPalettes.length === 0,
  );

  const visiblePalettes = showAllPalettes
    ? context.palettes
    : recommendedPalettes;

  // The collapsed grid intentionally hides non-recommended palettes
  // even when one of them is selected. Surface a tiny inline hint so
  // the operator can tell their previously-picked palette is just
  // hidden behind the "Show all palettes" button, rather than
  // silently lost.
  const hiddenSelectedPalette = useMemo(() => {
    if (showAllPalettes) return null;
    if (!design.palette) return null;
    if (recommended.has(design.palette)) return null;
    return (
      context.palettes.find((p) => p.id === design.palette) ?? null
    );
  }, [
    showAllPalettes,
    design.palette,
    recommended,
    context.palettes,
  ]);

  const toggleFormat = (id: string) => {
    const current = design.formats ?? [];
    if (current.includes(id)) {
      setDesign({ ...design, formats: current.filter((f) => f !== id) });
    } else {
      setDesign({ ...design, formats: [...current, id] });
    }
  };

  const canProceed =
    design.orientation &&
    design.mood &&
    design.style &&
    design.palette &&
    (design.formats?.length ?? 0) > 0;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5">
        <h2 className="text-base font-semibold text-slate-900 mb-3">Orientation</h2>
        <p className="text-sm text-slate-500 mb-3">
          Pick the base view — each thumbnail previews the orientation we'll
          aim for.
        </p>
        <div className="flex flex-wrap gap-2">
          {ORIENTATIONS.map((o) => {
            const thumb = pickOrientationThumb(
              o.id,
              context.merch_images,
              context.cover_images,
            );
            const active = design.orientation === o.id;
            return (
              <button
                key={o.id}
                onClick={() => setDesign({ ...design, orientation: o.id })}
                title={o.label}
                className={`group w-24 flex flex-col items-stretch text-left transition-transform ${
                  active ? 'scale-[1.02]' : 'hover:scale-[1.02]'
                }`}
              >
                <div
                  className={`aspect-video rounded-md overflow-hidden border bg-slate-100 transition-shadow ${
                    active
                      ? 'border-indigo-500 ring-2 ring-indigo-200 shadow-md'
                      : 'border-slate-200 group-hover:shadow-md'
                  }`}
                >
                  {thumb ? (
                    <img
                      src={thumb}
                      alt={o.label}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[9px] uppercase tracking-wide text-slate-400">
                      No preview
                    </div>
                  )}
                </div>
                <span
                  className={`mt-1 text-xs font-medium truncate ${
                    active ? 'text-indigo-700' : 'text-slate-700'
                  }`}
                >
                  {o.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5">
        <h2 className="text-base font-semibold text-slate-900 mb-3">Mood</h2>
        <div className="flex flex-wrap gap-2">
          {(context.moods.length > 0 ? context.moods : MOODS).map((m) => (
            <button
              key={m}
              onClick={() => setDesign({ ...design, mood: m as Mood })}
              className={chipClass(design.mood === m)}
            >
              {m}
            </button>
          ))}
        </div>
        {design.mood && MOOD_DESCRIPTIONS[design.mood as Mood] && (
          <p className="mt-3 text-sm text-slate-600 italic">
            {MOOD_DESCRIPTIONS[design.mood as Mood]}
          </p>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5">
        <h2 className="text-base font-semibold text-slate-900 mb-3">Style</h2>
        <div className="flex flex-wrap gap-2">
          {(context.styles.length > 0 ? context.styles : STYLES).map((s) => (
            <button
              key={s}
              onClick={() => setDesign({ ...design, style: s as Style })}
              className={chipClass(design.style === s)}
            >
              {s}
            </button>
          ))}
        </div>
        {design.style && STYLE_DESCRIPTIONS[design.style as Style] && (
          <p className="mt-3 text-sm text-slate-600 italic">
            {STYLE_DESCRIPTIONS[design.style as Style]}
          </p>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h2 className="text-base font-semibold text-slate-900">Palette</h2>
          {recommended.size > 0 && (
            <span className="text-xs text-slate-500">
              <span className="text-amber-500">★</span> recommended for{' '}
              {context.vehicle_info.color ?? 'this vehicle'}
            </span>
          )}
        </div>
        {hiddenSelectedPalette && (
          <div className="mb-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-center justify-between gap-2">
            <span>
              Current selection{' '}
              <span className="font-medium">
                {hiddenSelectedPalette.label}
              </span>{' '}
              is outside the recommended set and is hidden here.
            </span>
            <button
              onClick={() => setShowAllPalettes(true)}
              className="font-medium underline hover:no-underline whitespace-nowrap"
            >
              Show all palettes
            </button>
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {visiblePalettes.map((p) => (
            <PaletteSwatch
              key={p.id}
              palette={p}
              isActive={design.palette === p.id}
              isRecommended={recommended.has(p.id)}
              onClick={() => setDesign({ ...design, palette: p.id })}
            />
          ))}
        </div>
        {otherPalettes.length > 0 && recommendedPalettes.length > 0 && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={() => setShowAllPalettes((v) => !v)}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1"
            >
              {showAllPalettes
                ? 'Show only recommended'
                : `Show all palettes (${context.palettes.length})`}
              <span aria-hidden className="text-xs">
                {showAllPalettes ? '▴' : '▾'}
              </span>
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5">
        <h2 className="text-base font-semibold text-slate-900 mb-3">Formats</h2>
        <p className="text-sm text-slate-500 mb-3">
          Select one or more output formats — the engine renders each one
          separately.
        </p>
        <div className="flex flex-wrap gap-2">
          {context.formats.map((f) => {
            const active = (design.formats ?? []).includes(f.id);
            return (
              <button
                key={f.id}
                onClick={() => toggleFormat(f.id)}
                className={chipClass(active)}
              >
                <span className="block">{f.label}</span>
                <span className="block text-[10px] opacity-75">
                  {f.width}×{f.height} · {f.aspect_ratio}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5">
        <h2 className="text-base font-semibold text-slate-900 mb-3">Logo overlay</h2>
        <div className="flex flex-wrap items-start gap-4 mb-3">
          <div className="flex flex-col gap-2">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                setLogoFile(f);
                if (!f) {
                  setDesign({ ...design, logo_corner: 'none' });
                }
              }}
              className="text-sm"
            />
            {logoFile && (
              <span className="text-xs text-slate-600">
                {logoFile.name} ({Math.round(logoFile.size / 1024)} KB)
              </span>
            )}
          </div>
          {logoPreviewUrl && (
            <div className="flex flex-col items-start gap-1">
              <span className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">
                Preview
              </span>
              <div className="h-20 w-20 rounded-md border border-slate-200 bg-[conic-gradient(at_50%_50%,#f8fafc_0_25%,#e2e8f0_0_50%,#f8fafc_0_75%,#e2e8f0_0)] bg-[length:12px_12px] overflow-hidden flex items-center justify-center">
                <img
                  src={logoPreviewUrl}
                  alt="Logo preview"
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {LOGO_CORNERS.map((c) => (
            <button
              key={c.id}
              onClick={() => setDesign({ ...design, logo_corner: c.id })}
              className={chipClass(design.logo_corner === c.id)}
              disabled={c.id !== 'none' && !logoFile}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md"
        >
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed}
          className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Step 4 — Generate
// =============================================================================

function GenerateStep({
  state,
  context,
  logoFile,
  onBack,
  onSubmitted,
}: {
  state: WizardState;
  context: AdsContext;
  logoFile: File | null;
  onBack: () => void;
  // Reports the queued job back to the page wrapper. We hand back the
  // submitted ``scanId`` too because the wrapper clears its own state
  // immediately afterwards and would otherwise have nothing to put in
  // the "Job submitted" toast.
  onSubmitted: (jobId: string, scanId: string) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      let logoKey: string | null = null;
      if (logoFile && state.design.logo_corner && state.design.logo_corner !== 'none') {
        const upload = await uploadLogo(logoFile);
        logoKey = upload.logo_key;
      }

      const design = state.design as AdDesign;
      const created = await createAdsJob({
        scan_id: state.scanId,
        source: 'manual',
        metadata: state.metadata,
        info: state.info,
        design: {
          orientation: design.orientation,
          mood: design.mood,
          style: design.style,
          palette: design.palette,
          formats: design.formats,
          logo_corner: design.logo_corner,
        },
        logo_key: logoKey,
      });
      onSubmitted(created.job_id, state.scanId);
    } catch (ex) {
      log.error('submit failed', ex);
      setError(ex instanceof Error ? ex.message : 'Failed to submit job');
    } finally {
      setSubmitting(false);
    }
  };

  const designSummary = useMemo(() => {
    const d = state.design;
    return [
      `Orientation: ${d.orientation ?? '-'}`,
      `Mood: ${d.mood ?? '-'}`,
      `Style: ${d.style ?? '-'}`,
      `Palette: ${d.palette ?? '-'}`,
      `Formats: ${(d.formats ?? []).join(', ')}`,
      `Logo corner: ${d.logo_corner ?? '-'}`,
    ];
  }, [state.design]);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5">
        <h2 className="text-base font-semibold text-slate-900 mb-3">
          Review and submit
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <h3 className="text-xs font-semibold uppercase text-slate-500 mb-1">
              Vehicle
            </h3>
            <ul className="text-slate-700 space-y-0.5">
              {(Object.keys(state.metadata) as (keyof VehicleMetadata)[]).map(
                (k) => (
                  <li key={k}>
                    <span className="text-slate-500 capitalize">
                      {k.replace('_', ' ')}:
                    </span>{' '}
                    {state.metadata[k] || '—'}
                  </li>
                ),
              )}
            </ul>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase text-slate-500 mb-1">
              Listing
            </h3>
            <ul className="text-slate-700 space-y-0.5">
              {(Object.keys(state.info) as (keyof AdInfo)[]).map((k) => (
                <li key={k}>
                  <span className="text-slate-500 capitalize">
                    {String(k).replace('_', ' ')}:
                  </span>{' '}
                  {(state.info[k] as string | null) || '—'}
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-2">
            <h3 className="text-xs font-semibold uppercase text-slate-500 mb-1">
              Design
            </h3>
            <ul className="text-slate-700 space-y-0.5">
              {designSummary.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        </div>
        {context.uvcamp_link && (
          <a
            href={context.uvcamp_link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-3 text-xs text-indigo-600 hover:underline"
          >
            Open scan in UVCamp ↗
          </a>
        )}
      </div>

      {error && (
        <div className="px-3 py-2 rounded bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md"
          disabled={submitting}
        >
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50"
        >
          {submitting ? 'Submitting…' : 'Generate ads'}
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Generate page wrapper
// =============================================================================

interface SubmittedBanner {
  jobId: string;
  scanId: string;
}

export function AdsGeneratePage() {
  const [state, setState] = useState<WizardState>(() => loadPersistedState());
  const [context, setContext] = useState<AdsContext | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [step, setStep] = useState<Step>(1);
  // Non-persistent toast surfaced after a successful submit. Lives in
  // React state only (never written to localStorage) so a page reload
  // never shows a stale "Job submitted" banner.
  const [submitted, setSubmitted] = useState<SubmittedBanner | null>(null);

  useEffect(() => {
    persistState(state);
  }, [state]);

  // Auto-dismiss the success banner so it doesn't shadow the next submit.
  useEffect(() => {
    if (!submitted) return;
    const handle = window.setTimeout(() => setSubmitted(null), SUCCESS_BANNER_MS);
    return () => window.clearTimeout(handle);
  }, [submitted]);

  const updateMetadata = useCallback(
    (m: VehicleMetadata) => setState((s) => ({ ...s, metadata: m })),
    [],
  );
  const updateInfo = useCallback(
    (i: AdInfo) => setState((s) => ({ ...s, info: i })),
    [],
  );
  const updateDesign = useCallback(
    (d: Partial<AdDesign>) => setState((s) => ({ ...s, design: d })),
    [],
  );
  const updateScanId = useCallback(
    (sid: string) => setState((s) => ({ ...s, scanId: sid })),
    [],
  );

  // Once a job is queued we deliberately throw away every piece of
  // wizard state and snap back to Step 1. The operator monitors the
  // job from the History / Live Scans pages — keeping the just-
  // submitted job on this page would (a) make reloads resurrect a
  // stale review screen and (b) suggest the wizard is "owned" by one
  // job at a time, which it isn't.
  const handleSubmitted = useCallback((jobId: string, scanId: string) => {
    clearPersistedState();
    setState({ ...EMPTY_STATE });
    setContext(null);
    setLogoFile(null);
    setStep(1);
    setSubmitted({ jobId, scanId });
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Generate ads</h1>
      </div>

      {submitted && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start justify-between gap-3">
          <div className="text-sm text-emerald-800">
            <span className="font-medium">Job submitted.</span>{' '}
            Scan <span className="font-mono">{submitted.scanId}</span> · Job{' '}
            <span className="font-mono">{submitted.jobId.slice(0, 8)}…</span>{' '}
            —{' '}
            <Link
              to="/ads/history"
              className="font-medium text-emerald-900 underline hover:no-underline"
            >
              track progress in History
            </Link>
            .
          </div>
          <button
            onClick={() => setSubmitted(null)}
            className="text-emerald-700 hover:text-emerald-900 text-xs"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      <Stepper step={step} />

      {step === 1 && (
        <ContextStep
          scanId={state.scanId}
          setScanId={updateScanId}
          context={context}
          setContext={setContext}
          metadata={state.metadata}
          setMetadata={updateMetadata}
          onNext={() => setStep(2)}
        />
      )}

      {step === 2 && (
        <InfoStep
          info={state.info}
          setInfo={updateInfo}
          onBack={() => setStep(1)}
          onNext={() => setStep(3)}
        />
      )}

      {step === 3 && context && (
        <DesignStep
          context={context}
          design={state.design}
          setDesign={updateDesign}
          logoFile={logoFile}
          setLogoFile={setLogoFile}
          onBack={() => setStep(2)}
          onNext={() => setStep(4)}
        />
      )}
      {step === 3 && !context && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700">
          Context missing — go back to Step 1 and fetch the scan.
        </div>
      )}

      {step === 4 && context && (
        <GenerateStep
          state={state}
          context={context}
          logoFile={logoFile}
          onBack={() => setStep(3)}
          onSubmitted={handleSubmitted}
        />
      )}
      {step === 4 && !context && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700">
          Context missing — please restart the wizard.
        </div>
      )}
    </div>
  );
}
